import { errorResponse } from "../api-interface";
import apiService from "../api-service";

// ============================================================================
// Functions from fileUploadApi.ts
// ============================================================================

// Types
export interface UploadResponse {
  fileId?: string;
  fileName?: string;
  fileUrl?: string;
  token?: string;
}

export interface UploadConfig {
  workGroupId: string;
  clientId: string;
  origin: string;
}

export interface KeyMetricsResponse {
  total_files_analysed: number;
  average_compliance_score: number;
  compliant_files: number;
  total_issues_found: number;
}

export interface CheckWiseCount {
  check_name: string;
  pass: number;
  fail: number;
  pass_rate: number;
}

export interface FindingsSummaryResponse {
  total_checks: number;
  pass_rate: number;
  passed: number;
  failed: number;
  check_wise_count: CheckWiseCount[];
}

export interface FindingsSummaryParams {
  brand_id?: string[];
  channel_id?: string[];
  date_range?: string;
}

export interface Task {
  task_id: string;
  file_name: string;
  brand: string;
  channel: string;
  date: string;
  compliance_score: number;
  status: "Compliant" | "Needs Review";
  issues: number;
}

export interface TasksResponse {
  tasks: Task[];
  total_count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
}

export interface TasksParams {
  page_number?: number;
  task_id?: string;
  brand_id?: string[];
  channel_id?: string[];
  date_range?: string;
}

const USE_MOCK = false;

/**
 * Get upload configuration
 */
const getUploadConfig = (): UploadConfig => {
  return {
    workGroupId:
      import.meta.env.VITE_WORK_GROUP_ID ||
      "9ff74475-b302-4ab5-bec5-8c91c362fa1c",
    clientId:
      import.meta.env.VITE_FILE_CLIENT_ID ||
      "46f836d6-dc07-4e87-94d8-fde07087ebd4",
    origin: import.meta.env.VITE_ORIGIN || "contentqa_Input",
  };
};

/**
 * Mock file upload for development
 */
const mockUploadBlob = async (file: File): Promise<UploadResponse> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return {
    fileId: `mock-file-${Date.now()}`,
    fileName: file.name,
    fileUrl: URL.createObjectURL(file),
  };
};

/**
 * Upload binary content to the platform file store
 * @param file - File object to upload
 * @param token - Authentication token
 * @returns Parsed JSON response from the platform API
 */
export const uploadBlob = async (
  file: File,
  token: string,
): Promise<UploadResponse> => {
  if (USE_MOCK) {
    return mockUploadBlob(file);
  }
  if (!file) {
    throw new Error("File must not be empty");
  }

  if (!file.name || !file.name.trim()) {
    throw new Error("File name must not be blank");
  }

  const config = getUploadConfig();

  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("WorkGroupId", config.workGroupId);
  formData.append("ClientId", config.clientId);
  formData.append("Origin", config.origin);

  try {
    const response: any = await apiService.post({
      url: "filestorage/upload",
      data: formData,
      baseUrlKey: "platformServiceUrl",
      accessToken: token,
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error("Upload failed: network connection issue");
    }
  } catch (error: any) {
    console.error("File upload error:", error);
    throw error;
  }
};

/**
 * Create task without token API
 * @param payload - Task creation payload
 * @param token - Optional authentication token
 * @returns Task creation response
 */
export const createTaskNoTokenAPI = async (
  payload: any,
  token?: string,
): Promise<any> => {
  try {
    const response: any = await apiService.post({
      url: "tasks",
      data: payload,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      return errorResponse(`Creating task failed: network connection issue`);
    }
  } catch (error: any) {
    console.error("Task creation error:", error);
    return errorResponse(
      `Creating task failed: ${error?.message || String(error)}`,
    );
  }
};

/**
 * Get task status by task ID
 * @param taskId - The task ID returned from createTaskNoTokenAPI
 * @param token - Optional authentication token
 * @returns Task status response
 */
export const getTaskStatus = async (
  taskId: string,
  token?: string,
): Promise<any> => {
  try {
    const response: any = await apiService.get({
      url: `tasks/${taskId}/status`,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      return errorResponse(
        `Failed to get task status: network connection issue`,
      );
    }
  } catch (error: any) {
    console.error("Task status error:", error);
    return errorResponse(
      `Failed to get task status: ${error?.message || String(error)}`,
    );
  }
};

/**
 * Get task results by task ID
 * @param taskId - The task ID returned from createTaskNoTokenAPI
 * @param token - Optional authentication token
 * @returns Task results
 */
export const getTaskResults = async (
  taskId: string,
  token?: string,
): Promise<any> => {
  try {
    const response: any = await apiService.get({
      url: `tasks/${taskId}/results`,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      return errorResponse(
        `Failed to get task results: network connection issue`,
      );
    }
  } catch (error: any) {
    console.error("Task results error:", error);
    return errorResponse(
      `Failed to get task results: ${error?.message || String(error)}`,
    );
  }
};

/**
 * Get key metrics for dashboard
 * @param params - Optional filters for brand_id, channel_id, and date range
 * @param token - Optional authentication token
 * @returns Key metrics data
 */
export const getKeyMetrics = async (
  params?: FindingsSummaryParams,
  token?: string,
): Promise<KeyMetricsResponse> => {
  // Build query parameters
  const queryParams: any = {};
  if (params?.brand_id && params.brand_id.length > 0) {
    queryParams.brand_id =
      params.brand_id.length > 1 ? params.brand_id : params.brand_id[0];
  }
  if (params?.channel_id && params.channel_id.length > 0) {
    queryParams.channel_id =
      params.channel_id.length > 1 ? params.channel_id : params.channel_id[0];
  }
  if (params?.date_range) {
    queryParams.date_range = params.date_range;
  }

  try {
    const response: any = await apiService.get({
      url: "dashboard/key_metrics",
      params: queryParams,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error("Failed to get key metrics: network connection issue");
    }
  } catch (error: any) {
    console.error("Key metrics error:", error);
    throw error;
  }
};

/**
 * Get findings summary for dashboard
 * @param params - Optional filters for brand_id and channel_id
 * @param token - Optional authentication token
 * @returns Findings summary data
 */
export const getFindingsSummary = async (
  params?: FindingsSummaryParams,
  token?: string,
): Promise<FindingsSummaryResponse> => {
  // Build query parameters
  const queryParams: any = {};
  if (params?.brand_id && params.brand_id.length > 0) {
    queryParams.brand_id =
      params.brand_id.length > 1 ? params.brand_id : params.brand_id[0];
  }
  if (params?.channel_id && params.channel_id.length > 0) {
    queryParams.channel_id =
      params.channel_id.length > 1 ? params.channel_id : params.channel_id[0];
  }
  if (params?.date_range) {
    queryParams.date_range = params.date_range;
  }

  try {
    const response: any = await apiService.get({
      url: "dashboard/findings_summary",
      params: queryParams,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error(
        "Failed to get findings summary: network connection issue",
      );
    }
  } catch (error: any) {
    console.error("Findings summary error:", error);
    throw error;
  }
};

/**
 * Mock data generator for tasks
 */
// const generateMockTasks = (): Task[] => {
//   const brands = ["Reblozyl", "Camzyos", "Cobenfy"];
//   const channels = ["Static Banner", "Email HOE", "Email FTE", "Animated Banner"];
//   const tasks: Task[] = [];
//   for (let i = 1; i <= 23; i++) {
//     const brand = brands[Math.floor(Math.random() * brands.length)];
//     const channel = channels[Math.floor(Math.random() * channels.length)];
    
//     // Generate compliance score first, then determine status based on it
//     const complianceScore = Math.floor(Math.random() * 100);
//     const status: "Compliant" | "Needs Review" = complianceScore >= 90 ? "Compliant" : "Needs Review";
//     const issuesCount = status === "Compliant" ? 0 : Math.floor(Math.random() * 10) + 1;

//     tasks.push({
//       task_id: `TASK-${String(i).padStart(4, "0")}`,
//       file_name: `${brand.toLowerCase()}_campaign_${i}.pdf`,
//       brand,
//       channel,
//       date: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1)
//         .toISOString()
//         .split("T")[0],
//       compliance_score: complianceScore,
//       status,
//       issues_count: issuesCount,
//     });
//   }

//   return tasks;
// };

/**
 * Get tasks with pagination for dashboard
 * @param params - Optional filters and pagination parameters
 * @param token - Optional authentication token
 * @returns Tasks data with pagination info
 */
export const getTasks = async (
  params?: TasksParams,
  _token?: string,
): Promise<TasksResponse> => {
  // Mock implementation - simulating API delay
  // await new Promise((resolve) => setTimeout(resolve, 500));

  // const allTasks = generateMockTasks();
  // const pageSize = 5;
  // const currentPage = params?.page_number || 1;

  // // Apply filters
  // let filteredTasks = [...allTasks];

  // if (params?.task_id) {
  //   filteredTasks = filteredTasks.filter((task) =>
  //     task.task_id.toLowerCase().includes(params.task_id!.toLowerCase())
  //   );
  // }

  // if (params?.brand_id && params.brand_id.length > 0) {
  //   filteredTasks = filteredTasks.filter((task) =>
  //     params.brand_id!.includes(task.brand)
  //   );
  // }

  // if (params?.channel_id && params.channel_id.length > 0) {
  //   filteredTasks = filteredTasks.filter((task) =>
  //     params.channel_id!.includes(task.channel)
  //   );
  // }

  // if (params?.date_range) {
  //   // Parse date range (format: "YYYY-MM-DD,YYYY-MM-DD")
  //   const [startDate, endDate] = params.date_range.split(",");
  //   filteredTasks = filteredTasks.filter((task) => {
  //     return task.date >= startDate && task.date <= endDate;
  //   });
  // }

  // // Calculate pagination
  // const totalCount = filteredTasks.length;
  // const totalPages = Math.ceil(totalCount / pageSize);
  // const startIndex = (currentPage - 1) * pageSize;
  // const endIndex = startIndex + pageSize;
  // const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

  // return {
  //   tasks: paginatedTasks,
  //   total_count: totalCount,
  //   total_pages: totalPages,
  //   current_page: currentPage,
  //   page_size: pageSize,
  // };

  //Real API implementation (commented out for now)
  // Build query parameters
  const queryParams: any = {};
  
  if (params?.page_number) {
    queryParams.page_number = params.page_number;
  }
  if (params?.task_id) {
    queryParams.task_id = params.task_id;
  }
  if (params?.brand_id && params.brand_id.length > 0) {
    queryParams.brand_id =
      params.brand_id.length > 1 ? params.brand_id : params.brand_id[0];
  }
  if (params?.channel_id && params.channel_id.length > 0) {
    queryParams.channel_id =
      params.channel_id.length > 1 ? params.channel_id : params.channel_id[0];
  }
  if (params?.date_range) {
    queryParams.date_range = params.date_range;
  }

  try {
    const response: any = await apiService.get({
      url: "dashboard/tasks",
      params: queryParams,
      baseUrlKey: "apiUrl",
      accessToken: _token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error("Failed to get tasks: network connection issue");
    }
  } catch (error: any) {
    console.error("Tasks error:", error);
    throw error;
  }
};

